import React from 'react';

import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

type CategoryCount = {
    numCorrect: number;
    numQuestions: number;
};

type CategoryDetailLabelProps = {
    name: string;
    categories?: Record<string, CategoryCount>;
    active: boolean;
    onToggle: () => void;
    onClose: () => void;
};

/**
 * Root-category label for the class report's category breakdown. Hovering
 * shows the sub-category split; clicking pins it (the page owns which one is
 * pinned). Falls back to a plain label when there is nothing to break down.
 *
 * Styling, categories, thresholds and order mirror the individual report's
 * card (`TestSATReport` / `TestSHSATReport`, `testReport__levelDetailed*`).
 * Keep the two in step when either changes.
 *
 * One deliberate difference: the value is a percentage, not `correct/total`.
 * A class total runs to every student's answers, so the raw counts are large
 * and hard to read, and the panel is titled "(%correct)" already.
 */
function CategoryDetailLabel({ name, categories, active, onToggle, onClose }: CategoryDetailLabelProps) {
    // Clicking anywhere off a pinned card unpins it. Only the pinned label
    // reacts, so the other labels' handlers are inert.
    const labelRef = useOutsideClickHandler(() => active && onClose());

    // Server order, as the individual report does. The zero guard only keeps
    // the ratio below well defined; the server never emits an empty count.
    const rows = Object.entries(categories ?? {}).filter(([, count]) => count.numQuestions > 0);

    if (rows.length === 0) {
        return <div className='categoryDetailLabel__plain'>{name}</div>;
    }

    return (
        <div
            ref={labelRef}
            className={active ? 'categoryDetailLabel categoryDetailLabel--active' : 'categoryDetailLabel'}
            onClick={onToggle}
        >
            {name}
            <div className='categoryDetailLabel__card' onClick={event => event.stopPropagation()}>
                {rows.map(([subName, { numCorrect, numQuestions }]) => (
                    <div key={subName} className='categoryDetailLabel__row'>
                        <p>{subName}</p>
                        <p className={numCorrect / numQuestions >= 0.8
                            ? 'categoryDetailLabel__green'
                            : numCorrect / numQuestions >= 0.4
                            ? 'categoryDetailLabel__yellow'
                            : 'categoryDetailLabel__red'
                        }>
                            {Math.round(100 * numCorrect / numQuestions)}%
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default CategoryDetailLabel;
